import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Storefront, FileCsv, FilePdf, DownloadSimple, Pencil, ChatText,
  ArrowSquareOut, X, Trash, FunnelSimple,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";

const STATUSES = [
  "new", "under_review", "interview_scheduled", "shortlisted",
  "approved", "rejected", "partnered",
];

const TARGET_COUNTRIES = ["Canada", "UAE", "Saudi Arabia", "Indonesia", "India", "Argentina", "Other"];
const INDUSTRIES = [
  "Food & Beverage", "Retail", "Education", "Healthcare", "Recruitment",
  "Immigration", "E-Commerce", "Consulting", "Technology", "Manufacturing",
  "Import / Export", "Other",
];
const INVESTMENTS = [
  "Under $10,000", "$10,000 - $25,000", "$25,000 - $50,000",
  "$50,000 - $100,000", "$100,000 - $250,000", "$250,000+",
];

const STATUS_COLOR = {
  new: "bg-gos-subtle text-gos-charcoal",
  under_review: "bg-amber-50 text-amber-700",
  interview_scheduled: "bg-blue-50 text-blue-700",
  shortlisted: "bg-indigo-50 text-indigo-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
  partnered: "bg-gos-navy text-white",
};

export default function AdminFranchise() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filters, setFilters] = useState({
    status: "", target_country: "", preferred_industry: "",
    investment_capacity: "", min_years: "", search: "",
  });
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.append(k, v); });
      const [a, s] = await Promise.all([
        api.get(`/admin/franchise?${qs.toString()}`),
        api.get("/admin/franchise/stats"),
      ]);
      setApps(a.data); setStats(s.data);
    } catch (e) {
      toast.error("Failed to load applications");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/franchise/${id}`, { status });
      toast.success("Status updated");
      refresh();
      if (open?.id === id) setOpen((o) => ({ ...o, status }));
    } catch (e) { toast.error("Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this application? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/franchise/${id}`);
      toast.success("Deleted");
      setOpen(null);
      refresh();
    } catch (e) { toast.error("Delete failed"); }
  };

  const exportFile = (kind) => {
    const token = localStorage.getItem("gos_token");
    // We embed the token in URL not allowed — open via fetch-blob
    fetch(`${API}/admin/franchise/export.${kind}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.blob() : Promise.reject(r.statusText))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `franchise-applications.${kind}`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export failed"));
  };

  const updFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-franchise">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <Storefront size={14} weight="duotone" /> Franchise Applications
          </div>
          <h2 className="font-display text-2xl text-gos-charcoal">{stats.total} total</h2>
          <div className="flex flex-wrap gap-1 mt-2 text-[10px] uppercase tracking-widest">
            {Object.entries(stats.by_status || {}).map(([k, v]) => v > 0 && (
              <span key={k} className={`px-2 py-0.5 rounded-sm ${STATUS_COLOR[k] || "bg-gos-subtle"}`}>
                {k.replace(/_/g, " ")}: {v}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportFile("csv")} data-testid="franchise-export-csv" className="btn-secondary text-xs !py-2">
            <FileCsv size={14} weight="duotone" /> Export CSV
          </button>
          <button onClick={() => exportFile("pdf")} data-testid="franchise-export-pdf" className="btn-secondary text-xs !py-2">
            <FilePdf size={14} weight="duotone" /> Export PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gos-border rounded-sm p-4 mb-6">
        <div className="overline mb-3 flex items-center gap-2"><FunnelSimple size={14} /> Filters</div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Select label="Status" value={filters.status} onChange={(v) => updFilter("status", v)} testid="fr-filter-status">
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </Select>
          <Select label="Target Country" value={filters.target_country} onChange={(v) => updFilter("target_country", v)} testid="fr-filter-country">
            <option value="">All</option>
            {TARGET_COUNTRIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Select label="Industry" value={filters.preferred_industry} onChange={(v) => updFilter("preferred_industry", v)} testid="fr-filter-industry">
            <option value="">All</option>
            {INDUSTRIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Select label="Investment" value={filters.investment_capacity} onChange={(v) => updFilter("investment_capacity", v)} testid="fr-filter-investment">
            <option value="">All</option>
            {INVESTMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <NumField label="Min Years Exp." value={filters.min_years} onChange={(v) => updFilter("min_years", v)} testid="fr-filter-years" />
          <TextField label="Search (name / email)" value={filters.search} onChange={(v) => updFilter("search", v)} testid="fr-filter-search" />
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-gos-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gos-subtle text-xs uppercase tracking-widest text-gos-navy">
            <tr>
              <th className="text-left p-3">Applicant</th>
              <th className="text-left p-3">Interest</th>
              <th className="text-left p-3">Target</th>
              <th className="text-left p-3">Investment</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-gos-muted">Loading…</td></tr>
            )}
            {!loading && apps.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-gos-muted">No applications match these filters.</td></tr>
            )}
            {apps.map((a) => (
              <tr key={a.id} data-testid={`franchise-row-${a.app_id}`} className="border-t border-gos-border hover:bg-gos-subtle/30">
                <td className="p-3">
                  <div className="text-gos-charcoal font-medium">{a.full_name}</div>
                  <div className="text-xs text-gos-muted">{a.email} · {a.city}, {a.country_of_residence}</div>
                </td>
                <td className="p-3 text-gos-slate text-xs">
                  <div>{a.interest_type}</div>
                  <div className="text-gos-muted">{a.preferred_industry}</div>
                </td>
                <td className="p-3 text-gos-slate text-xs">
                  <div>{a.target_country}</div>
                  <div className="text-gos-muted">{a.target_city}</div>
                </td>
                <td className="p-3 text-xs text-gos-slate">{a.investment_capacity}</td>
                <td className="p-3">
                  <select
                    value={a.status} onChange={(e) => updateStatus(a.id, e.target.value)}
                    data-testid={`fr-status-${a.app_id}`}
                    className={`text-xs px-2 py-1 rounded-sm border border-gos-border ${STATUS_COLOR[a.status] || ""}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                </td>
                <td className="p-3 text-xs text-gos-muted">{(a.created_at || "").slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <button
                    onClick={() => setOpen(a)}
                    data-testid={`fr-open-${a.app_id}`}
                    className="text-gos-navy hover:opacity-70 inline-flex items-center gap-1 text-xs"
                  >
                    <ArrowSquareOut size={14} /> Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <DetailDrawer
          app={open}
          onClose={() => setOpen(null)}
          onChanged={refresh}
          onDelete={() => del(open.id)}
          onStatus={(s) => updateStatus(open.id, s)}
        />
      )}
    </div>
  );
}

function DetailDrawer({ app, onClose, onChanged, onDelete, onStatus }) {
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    interest_type: app.interest_type || "",
    preferred_industry: app.preferred_industry || "",
    target_country: app.target_country || "",
    investment_capacity: app.investment_capacity || "",
  });

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/franchise/${app.id}/notes`, { text: note.trim() });
      setNote("");
      toast.success("Note added");
      onChanged();
    } catch (e) { toast.error("Could not save note"); }
  };

  const saveDraft = async () => {
    try {
      await api.patch(`/admin/franchise/${app.id}`, draft);
      toast.success("Saved");
      setEditing(false);
      onChanged();
    } catch (e) { toast.error("Save failed"); }
  };

  const downloadFile = (which) => {
    const token = localStorage.getItem("gos_token");
    fetch(`${API}/admin/franchise/${app.id}/file/${which}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.blob() : Promise.reject())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const f = app.files?.[which];
        const a = document.createElement("a");
        a.href = url; a.download = f?.original_filename || which; document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end" onClick={onClose} data-testid="fr-detail">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gos-border p-5 flex items-center justify-between z-10">
          <div>
            <div className="overline">Application · {app.app_id?.slice(0, 8)}</div>
            <h3 className="font-display text-2xl text-gos-charcoal">{app.full_name}</h3>
          </div>
          <button onClick={onClose} data-testid="fr-detail-close" className="text-gos-slate hover:text-gos-navy"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s} onClick={() => onStatus(s)}
                data-testid={`fr-detail-status-${s}`}
                className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm border ${
                  app.status === s ? "bg-gos-navy text-white border-gos-navy" : "border-gos-border text-gos-slate hover:border-gos-navy"
                }`}
              >
                {s.replace(/_/g, " ")}
              </button>
            ))}
          </div>

          {/* Contact */}
          <Section title="Contact">
            <Row k="Email" v={<a href={`mailto:${app.email}`} className="text-gos-navy underline">{app.email}</a>} />
            <Row k="Phone / WhatsApp" v={<a href={`tel:${app.phone}`} className="text-gos-navy underline">{app.phone}</a>} />
            <Row k="Location" v={`${app.city}, ${app.country_of_residence}`} />
            <Row k="Nationality" v={app.nationality} />
            {app.linkedin_url && <Row k="LinkedIn" v={<a className="text-gos-navy underline" href={app.linkedin_url} target="_blank" rel="noreferrer">View profile</a>} />}
          </Section>

          {/* Interest */}
          <Section
            title="Franchise Interest"
            action={<button onClick={() => setEditing((v) => !v)} className="text-xs text-gos-navy inline-flex items-center gap-1"><Pencil size={12} /> {editing ? "Cancel" : "Edit"}</button>}
          >
            {editing ? (
              <div className="space-y-3">
                <DraftField label="Interest" value={draft.interest_type} onChange={(v) => setDraft({ ...draft, interest_type: v })} />
                <DraftField label="Industry" value={draft.preferred_industry} onChange={(v) => setDraft({ ...draft, preferred_industry: v })} />
                <DraftField label="Target Country" value={draft.target_country} onChange={(v) => setDraft({ ...draft, target_country: v })} />
                <DraftField label="Investment" value={draft.investment_capacity} onChange={(v) => setDraft({ ...draft, investment_capacity: v })} />
                <button onClick={saveDraft} className="btn-primary text-xs !py-2">Save changes</button>
              </div>
            ) : (
              <>
                <Row k="Interest" v={app.interest_type} />
                <Row k="Industry" v={app.preferred_industry} />
                <Row k="Target country / city" v={`${app.target_country}${app.target_city ? " · " + app.target_city : ""}`} />
                <Row k="Investment capacity" v={app.investment_capacity} />
                <Row k="Timeline" v={app.timeline} />
              </>
            )}
          </Section>

          {/* Business */}
          <Section title="Business Background">
            <Row k="Occupation" v={app.current_occupation || "—"} />
            <Row k="Current business" v={app.current_business_name || "—"} />
            <Row k="Years experience" v={app.years_of_experience || "—"} />
            <Row k="Industry experience" v={app.industry_experience || "—"} />
            <Row k="Owned business before?" v={app.owned_business_before ? "Yes" : "No"} />
            <Row k="Managed staff before?" v={app.managed_staff_before ? "Yes" : "No"} />
            <Row k="Open to other opportunities?" v={app.cross_promote_consent ? "Yes" : "No"} />
          </Section>

          {/* Free text */}
          {(app.motivation || app.skills_resources) && (
            <Section title="Narrative">
              {app.motivation && <p className="text-sm text-gos-slate leading-relaxed"><b>Why interested:</b> {app.motivation}</p>}
              {app.skills_resources && <p className="text-sm text-gos-slate leading-relaxed mt-3"><b>Skills / resources:</b> {app.skills_resources}</p>}
            </Section>
          )}

          {/* Files */}
          {(app.files?.resume || app.files?.business_proposal) && (
            <Section title="Attachments">
              {app.files?.resume && (
                <button onClick={() => downloadFile("resume")} data-testid="fr-dl-resume" className="btn-secondary text-xs !py-2">
                  <DownloadSimple size={14} /> Resume — {app.files.resume.original_filename}
                </button>
              )}
              {app.files?.business_proposal && (
                <button onClick={() => downloadFile("business_proposal")} data-testid="fr-dl-proposal" className="btn-secondary text-xs !py-2 ml-2">
                  <DownloadSimple size={14} /> Business proposal — {app.files.business_proposal.original_filename}
                </button>
              )}
            </Section>
          )}

          {/* Notes */}
          <Section title="Internal Notes">
            <div className="space-y-2 mb-3">
              {(app.admin_notes || []).length === 0 && (
                <div className="text-xs text-gos-muted">No notes yet.</div>
              )}
              {(app.admin_notes || []).map((n, i) => (
                <div key={`${n.at || "note"}-${i}`} className="border border-gos-border rounded-sm px-3 py-2 text-sm">
                  <div className="text-gos-charcoal whitespace-pre-wrap">{n.text}</div>
                  <div className="text-[10px] text-gos-muted mt-1">{n.by} · {(n.at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note…" data-testid="fr-note-input"
                className="flex-1 border border-gos-border rounded-sm px-3 py-2 text-sm"
                onKeyDown={(e) => e.key === "Enter" && addNote()}
              />
              <button onClick={addNote} data-testid="fr-note-add" className="btn-primary text-xs !py-2"><ChatText size={14} /> Add</button>
            </div>
          </Section>

          <div className="pt-4 border-t border-gos-border">
            <button onClick={onDelete} data-testid="fr-detail-delete" className="text-rose-600 text-xs inline-flex items-center gap-1 hover:underline">
              <Trash size={14} /> Delete application
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">{title}</div>
        {action}
      </div>
      <div className="bg-gos-subtle/40 border border-gos-border rounded-sm p-4 space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 text-sm py-0.5">
      <span className="text-gos-muted text-xs uppercase tracking-widest">{k}</span>
      <span className="text-gos-charcoal text-right">{v}</span>
    </div>
  );
}

function DraftField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}

function Select({ label, value, onChange, testid, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm bg-white">
        {children}
      </select>
    </div>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <input type="number" min="0" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm" />
    </div>
  );
}
function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm" />
    </div>
  );
}
